import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import TodaysLessons from '@/components/TodaysLessons';
import { 
  Calculator, BookOpen, FlaskConical, Star, Flame, Trophy,
  ChevronRight, Sparkles, Target, Gamepad2, FileText, PenTool, Clock, Video, BarChart3, BookMarked
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';

const StudentDashboard = () => {
  const { user, api } = useAuth();
  const [stats, setStats] = useState(null);
  const [challenges, setChallenges] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, challengesRes] = await Promise.all([
          api.get('/v2/student/stats'),
          api.get('/challenges/weekly')
        ]);
        setStats(statsRes.data);
        setChallenges(challengesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api]);

  const subjects = [
    { id: 'maths', name: 'Maths', icon: Calculator, color: 'amber', desc: 'Numbers, algebra & more' },
    { id: 'english', name: 'English', icon: BookOpen, color: 'pink', desc: 'Reading, writing & grammar' },
    { id: 'science', name: 'Science', icon: FlaskConical, color: 'emerald', desc: 'Biology, chemistry & physics' },
  ];

  const keyStageMap = {
    ks1: { label: 'KS1', color: 'green' },
    ks2: { label: 'KS2', color: 'blue' },
    ks3: { label: 'KS3', color: 'purple' },
    ks4: { label: 'KS4', color: 'orange' },
    esol: { label: 'ESOL', color: 'pink' },
    '11plus': { label: '11+', color: 'amber' },
  };

  const userKS = keyStageMap[user?.key_stage] || keyStageMap.ks2;
  const allocatedSubjects = user?.allocated_subjects || ['maths', 'english', 'science'];

  return (
    <StudentLayout>
      {/* Welcome Card */}
      <div className="student-card mb-8" data-testid="welcome-card">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-purple-200 rounded-2xl border-2 border-gray-800 flex items-center justify-center text-4xl animate-float">
              👋
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-800">
                Hey {user?.first_name}!
              </h1>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <span className={`px-3 py-1 rounded-full font-bold text-sm ${userKS.color}-badge border-2`}>
                  {userKS.label} • Year {user?.year_group}
                </span>
                <div className="flex items-center gap-1 text-orange-500">
                  <Flame className="w-5 h-5" />
                  <span className="font-bold">{user?.streak_days || 0} day streak!</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 text-purple-500">
                <Star className="w-8 h-8" />
                <span className="font-heading text-4xl font-bold">{user?.total_points || 0}</span>
              </div>
              <p className="text-slate-500 font-semibold">Points</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 text-amber-500">
                <Trophy className="w-8 h-8" />
                <span className="font-heading text-4xl font-bold">{stats?.achievements_count || 0}</span>
              </div>
              <p className="text-slate-500 font-semibold">Badges</p>
            </div>
          </div>
        </div>
      </div>

      {/* Today's Learning Section */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="md:col-span-2">
          <TodaysLessons />
        </div>
        <div className="space-y-4">
          {/* Weekly Challenge Mini Card */}
          <Link to="/student/challenges" data-testid="weekly-challenge-mini">
            <div className="student-card bg-gradient-to-br from-purple-500 to-pink-500 text-white cursor-pointer h-full">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-bold">Weekly Challenge</h3>
                  {challenges && (
                    <p className="text-purple-100 text-sm">{challenges.completed_count}/{challenges.total_count} completed</p>
                  )}
                </div>
              </div>
              {challenges && (
                <Progress 
                  value={(challenges.completed_count / challenges.total_count) * 100} 
                  className="h-2 bg-white/20"
                />
              )}
            </div>
          </Link>
          
          {/* Streak Card */}
          <div className="student-card bg-gradient-to-br from-amber-400 to-orange-500 text-white">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Flame className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-heading text-2xl font-bold">{user?.streak_days || 0} Days</h3>
                <p className="text-amber-100 text-sm">Learning streak!</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Subjects Grid - Display Only */}
      <h2 className="font-heading text-2xl font-bold text-slate-800 mb-4">Your Subjects</h2>
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {subjects.filter(s => allocatedSubjects.includes(s.id)).map(subject => {
          const lessonsCompleted = stats?.stats?.subject_breakdown?.[subject.id] || 0;
          
          return (
            <div 
              key={subject.id} 
              className={`student-card bg-${subject.color}-50 border-${subject.color}-300`}
              data-testid={`subject-${subject.id}`}
            >
              <div className={`w-14 h-14 bg-${subject.color}-400 rounded-2xl border-2 border-gray-800 flex items-center justify-center mb-4`}>
                <subject.icon className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold text-slate-800 mb-1">{subject.name}</h3>
              <p className="text-slate-600 text-sm mb-4">{subject.desc}</p>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-500" />
                <span className="text-sm font-semibold text-slate-600">{lessonsCompleted} lessons done</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Special Sections */}
      {(user?.key_stage === '11plus' || allocatedSubjects.includes('11plus')) && (
        <div className="mb-8">
          <h2 className="font-heading text-2xl font-bold text-slate-800 mb-4">11+ Preparation</h2>
          <Link to="/student/11plus" data-testid="11plus-card">
            <div className="student-card bg-gradient-to-r from-purple-500 to-pink-500 text-white cursor-pointer">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold">11+ Exam Prep</h3>
                  <p className="text-white/80">Verbal & Non-Verbal Reasoning, Maths, English</p>
                </div>
                <ChevronRight className="w-6 h-6 text-white/60 ml-auto" />
              </div>
            </div>
          </Link>
        </div>
      )}

      {allocatedSubjects.includes('esol') && (
        <div className="mb-8">
          <h2 className="font-heading text-2xl font-bold text-slate-800 mb-4">ESOL</h2>
          <Link to="/student/subject/esol/esol">
            <div className="student-card bg-pink-50 border-pink-300 cursor-pointer">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-pink-400 rounded-2xl border-2 border-gray-800 flex items-center justify-center">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-slate-800">English for Speakers of Other Languages</h3>
                  <p className="text-slate-600">Build your English skills step by step</p>
                </div>
                <ChevronRight className="w-6 h-6 text-slate-400 ml-auto" />
              </div>
            </div>
          </Link>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Link to="/student/reading" data-testid="reading-adventure-card">
          <div className="student-card bg-gradient-to-br from-violet-600 to-purple-700 text-white cursor-pointer group h-full">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <BookMarked className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading font-bold">Reading Adventures</h3>
                <p className="text-purple-200 text-sm">Interactive stories!</p>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/student/times-tables" data-testid="times-tables-card">
          <div className="student-card bg-gradient-to-br from-indigo-500 to-purple-600 text-white cursor-pointer group h-full">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Calculator className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading font-bold">Times Tables</h3>
                <p className="text-indigo-100 text-sm">Master 1-12!</p>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/student/challenges" data-testid="challenges-card">
          <div className="student-card bg-gradient-to-br from-purple-500 to-pink-500 text-white cursor-pointer group h-full">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-heading font-bold">Weekly Challenges</h3>
                {challenges && (
                  <p className="text-purple-100 text-sm">{challenges.completed_count}/{challenges.total_count} done</p>
                )}
              </div>
            </div>
            {challenges && (
              <Progress 
                value={(challenges.completed_count / challenges.total_count) * 100} 
                className="h-2 bg-white/20"
              />
            )}
          </div>
        </Link>

        <Link to="/student/games" data-testid="games-card">
          <div className="student-card bg-purple-50 border-purple-300 cursor-pointer group h-full">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500 rounded-xl border-2 border-gray-800 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Gamepad2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-slate-800">Games</h3>
                <p className="text-slate-600 text-sm">Play & earn!</p>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/student/essay" data-testid="essay-card">
          <div className="student-card bg-pink-50 border-pink-300 cursor-pointer group h-full">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pink-500 rounded-xl border-2 border-gray-800 flex items-center justify-center group-hover:scale-110 transition-transform">
                <PenTool className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-slate-800">Essay Writing</h3>
                <p className="text-slate-600 text-sm">AI feedback!</p>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/student/exams" data-testid="exams-card">
          <div className="student-card bg-emerald-50 border-emerald-300 cursor-pointer group h-full">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500 rounded-xl border-2 border-gray-800 flex items-center justify-center group-hover:scale-110 transition-transform">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-slate-800">Exam Practice</h3>
                <p className="text-slate-600 text-sm">SATs & GCSE</p>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/tutoring" data-testid="tutoring-card">
          <div className="student-card bg-blue-50 border-blue-300 cursor-pointer group h-full">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-xl border-2 border-gray-800 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Video className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-slate-800">Live Tutoring</h3>
                <p className="text-slate-600 text-sm">Join sessions</p>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/student/analytics" data-testid="analytics-card">
          <div className="student-card bg-indigo-50 border-indigo-300 cursor-pointer group h-full">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-500 rounded-xl border-2 border-gray-800 flex items-center justify-center group-hover:scale-110 transition-transform">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-slate-800">My Analytics</h3>
                <p className="text-slate-600 text-sm">Track progress</p>
              </div>
            </div>
          </div>
        </Link>
      </div>
    </StudentLayout>
  );
};

export default StudentDashboard;
